// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 11-observability.md#app-usage-collection
// Usage-analytics band for the app-detail Monitoring tab: active users +
// request volume over a 1d/7d/30d window with daily trend charts and a
// last-active line, read from api.usage.getAppUsage (the /auth/check-metered
// rollups). IT-only, like the read it consumes — the parent tab gates it.
import * as React from 'react';

import { Users } from 'lucide-react';

import { formatCountFull } from '@/components/greenlight/formatCount';
import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { relativeTime, useTick } from '@/components/greenlight/relativeTime';
import { AD_CARD, AD_CARD_HD } from '@/components/greenlight/surfaces';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { metricsQueryOptions } from '@/lib/polling';
import type { AppUsage } from '@greenlight/shared';

import { AdEyebrow, AdStatTile } from './tab-primitives';

type WindowKey = '1d' | '7d' | '30d';

const WINDOWS: Record<WindowKey, { label: string; days: number }> = {
  '1d': { label: '1d', days: 1 },
  '7d': { label: '7d', days: 7 },
  '30d': { label: '30d', days: 30 },
};
const WINDOW_ORDER: readonly WindowKey[] = ['1d', '7d', '30d'];

// Session-scoped like the metrics-history range: survives tab switches
// (the tab unmounts), deliberately not a reload.
function readStoredWindow(): WindowKey {
  try {
    const v = sessionStorage.getItem('gl-usage-window');
    return v !== null && v in WINDOWS ? (v as WindowKey) : '30d';
  } catch {
    return '30d';
  }
}

const DAY_MS = 86_400_000;

function formatDayLabel(day: string): string {
  const t = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(t)) return day;
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// The API series is sparse — days with no authenticated requests have no
// point. Zero-fill across [from, to] so an idle stretch draws as a flat
// baseline rather than being skipped (usage ≠ resource metrics, where a gap
// means "not collected").
export function denseDailySeries(usage: Pick<AppUsage, 'from' | 'to' | 'series'>): {
  users: number[];
  requests: number[];
  labels: string[];
} {
  const byDay = new Map(usage.series.map((p) => [p.day, p]));
  const users: number[] = [];
  const requests: number[] = [];
  const labels: string[] = [];
  const start = Date.parse(`${usage.from}T00:00:00Z`);
  const end = Date.parse(`${usage.to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return { users, requests, labels };
  // Bound defends against a malformed range; the API window caps at 60 days.
  for (let t = start, i = 0; t <= end && i < 90; t += DAY_MS, i++) {
    const day = new Date(t).toISOString().slice(0, 10);
    const p = byDay.get(day);
    users.push(p?.active_user_count ?? 0);
    requests.push(p?.request_count ?? 0);
    labels.push(formatDayLabel(day));
  }
  return { users, requests, labels };
}

export function AppUsagePanel({ appId }: { appId: string }): React.ReactElement {
  const [windowKey, setWindowKeyState] = React.useState<WindowKey>(readStoredWindow);
  const setWindowKey = (k: WindowKey): void => {
    setWindowKeyState(k);
    try {
      sessionStorage.setItem('gl-usage-window', k);
    } catch {
      // Storage unavailable (private mode/quota) — in-memory state still works.
    }
  };

  const windowDays = WINDOWS[windowKey].days;
  const key = ['app-usage', appId, windowDays];
  // Rollups flush at minute granularity; within that a refetch can't return
  // newer data, so revisits within the tab serve the cache.
  const { data, isLoading, isError } = api.usage.getAppUsage.useQuery(
    key,
    { params: { id: appId }, query: { window_days: windowDays } },
    { ...metricsQueryOptions, queryKey: key },
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="usage-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  } else if (isError || !data || data.status !== 200) {
    body = (
      <Alert variant="destructive" data-testid="usage-error">
        <AlertTitle>Couldn't load usage</AlertTitle>
        <AlertDescription>Usage analytics are unavailable right now.</AlertDescription>
      </Alert>
    );
  } else {
    body = <UsageContent usage={data.body} />;
  }

  return (
    <div className={cn(AD_CARD, 'mb-9')} data-testid="usage-panel">
      <div className={AD_CARD_HD}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-gl-icon bg-glow text-verdant">
              <Users size={14} strokeWidth={1.6} aria-hidden />
            </span>
            <span className="text-[14px] font-semibold tracking-[-0.01em] text-foreground">
              Usage for this app
            </span>
          </div>
          <p className="mt-1 pl-9 text-[12px] text-muted-foreground">
            Distinct people and authenticated requests for this app only — not org-wide.
          </p>
        </div>
        <div
          className="inline-flex items-center gap-1 rounded-gl bg-muted/60 p-0.5"
          role="group"
          aria-label="Usage window"
        >
          {WINDOW_ORDER.map((k) => {
            const active = k === windowKey;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={active}
                onClick={() => setWindowKey(k)}
                className={cn(
                  'h-7 rounded-gl px-2.5 text-[12px] transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  active
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : 'font-medium text-muted-foreground hover:text-foreground',
                )}
              >
                {WINDOWS[k].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 md:p-6">{body}</div>
    </div>
  );
}

function UsageContent({ usage }: { usage: AppUsage }): React.ReactElement {
  useTick(30_000);

  if (usage.last_active_at === null && usage.totals.request_count === 0) {
    return (
      <div
        className="rounded-gl-icon border border-border p-6 text-center text-sm text-muted-foreground"
        data-testid="usage-empty"
      >
        No usage recorded for this app yet. Activity appears once someone signs in through
        Greenlight SSO.
      </div>
    );
  }

  const dense = denseDailySeries(usage);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="usage-totals">
        <AdStatTile
          label="Active users (this app)"
          value={formatCountFull(usage.totals.active_users)}
          sub={`distinct people · last ${usage.window_days}d`}
        />
        <AdStatTile
          label="Authenticated requests"
          value={formatCountFull(usage.totals.request_count)}
          sub={`SSO hits · last ${usage.window_days}d`}
        />
        <AdStatTile
          label="Last active"
          value={
            usage.last_active_at ? (
              <span className="text-[22px]">{relativeTime(usage.last_active_at)}</span>
            ) : (
              '—'
            )
          }
        />
      </div>

      {dense.requests.length >= 2 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="usage-trends">
          <UsageTrend label="Active users / day" data={dense.users} labels={dense.labels} />
          <UsageTrend
            label="Authenticated requests / day"
            data={dense.requests}
            labels={dense.labels}
          />
        </div>
      )}
    </div>
  );
}

function UsageTrend({
  label,
  data,
  labels,
}: {
  label: string;
  data: number[];
  labels: string[];
}): React.ReactElement {
  const peak = data.reduce((m, v) => Math.max(m, v), 0);
  return (
    <div className="rounded-gl-icon border border-border p-3.5">
      <AdEyebrow className="mb-2">{label}</AdEyebrow>
      <MetricSparkline
        data={data}
        pointLabels={labels}
        showAxes
        formatValue={formatCountFull}
        ariaLabel={label}
        idleAtZero
      />
      <p className="mt-2 font-mono text-[11.5px] text-muted-foreground">
        peak {formatCountFull(peak)}
      </p>
    </div>
  );
}
